import { gql, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@apollo/orbit';
import { ModalTitle } from '@apollo/orbit/src/components/ModalTitle/ModalTitle';
import React, { useCallback, useState } from 'react';

import { Form as FormWrapper } from 'src/components/common/form/Form';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useBulkSelect } from 'src/hooks/useBulkSelect';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { persistedQueryListQuery } from '../queries';
import { BulkSelectItem } from '../shared';

export const PersistedQueryBulkDeleteModal = ({
  isOpen,
  onClose,
  graphId,
  listId,
  bulkSelectState,
  dispatchBulkSelectAction,
  filters,
  totalSelectedItems,
}: {
  isOpen: boolean;
  onClose: () => void;
  graphId: string;
  listId: string;
  filters: GraphQLTypes.PersistedQueryFilterInput;
  totalSelectedItems: number;
} & ReturnType<typeof useBulkSelect<BulkSelectItem>>) => {
  const [errorMessage, setErrorMessage] = useState('');

  const trackCustomEvent = useTrackCustomEvent();

  const [
    deleteIndividualOperations,
    { loading: deleteIndividualOperationsLoading },
  ] = useMutation<
    GraphQLTypes.PersistedQueryIndividualDeleteMutation,
    GraphQLTypes.PersistedQueryIndividualDeleteMutationVariables
  >(gql`
    mutation PersistedQueryIndividualDeleteMutation(
      $graphId: ID!
      $listId: ID!
      $remove: [PersistedQueryIdInput!]
    ) {
      graph(id: $graphId) {
        id
        persistedQueryList(id: $listId) {
          id
          publishOperations(remove: $remove) {
            ... on PublishOperationsResult {
              build {
                id
                totalOperationsInList
              }
              unchanged
            }
            ... on PermissionError {
              message
            }
            ... on CannotModifyOperationBodyError {
              message
            }
          }
        }
      }
    }
  `);

  const [deleteBulkOperations, { loading: deleteBulkOperationsLoading }] =
    useMutation<
      GraphQLTypes.PersistedQueryBulkDeleteMutation,
      GraphQLTypes.PersistedQueryBulkDeleteMutationVariables
    >(gql`
      mutation PersistedQueryBulkDeleteMutation(
        $graphId: ID!
        $listId: ID!
        $filter: PersistedQueryFilterInput!
        $exclude: [PersistedQueryIdInput!]
      ) {
        graph(id: $graphId) {
          id
          persistedQueryList(id: $listId) {
            id
            deleteOperationsByFilter(filter: $filter, exclude: $exclude) {
              ... on DeleteOperationsByFilterResult {
                build {
                  id
                  totalOperationsInList
                }
              }
              ... on PermissionError {
                message
              }
            }
          }
        }
      }
    `);

  const onSubmit = useCallback(async () => {
    if (bulkSelectState.state === 'individual') {
      const { data } = await deleteIndividualOperations({
        variables: {
          graphId,
          listId,
          remove: bulkSelectState.toggledItems,
        },
        refetchQueries: [persistedQueryListQuery],
        awaitRefetchQueries: true,
      });

      const publishOperationsResult =
        data?.graph?.persistedQueryList.publishOperations;
      if (publishOperationsResult?.__typename === 'PublishOperationsResult') {
        trackCustomEvent({
          action: 'user_action',
          category: 'Persisted Queries',
          label: 'Delete operations',
          secondaryLabel: `deletionType:${bulkSelectState.state}`,
        });
        onClose();
        dispatchBulkSelectAction({ type: 'reset' });
      } else if (
        publishOperationsResult?.__typename === 'PermissionError' ||
        publishOperationsResult?.__typename ===
          'CannotModifyOperationBodyError' ||
        !publishOperationsResult
      ) {
        setErrorMessage(
          publishOperationsResult?.message ?? 'An error has occurred',
        );
      } else {
        setErrorMessage(
          assertUnreachableOrReturnDefault(
            publishOperationsResult,
            'An error has occurred',
          ),
        );
      }
    } else if (bulkSelectState.state === 'bulk') {
      const { data } = await deleteBulkOperations({
        variables: {
          graphId,
          listId,
          filter: filters,
          exclude: bulkSelectState.toggledItems,
        },
        refetchQueries: [persistedQueryListQuery],
        awaitRefetchQueries: true,
      });

      const deleteOperationsByFilterResult =
        data?.graph?.persistedQueryList.deleteOperationsByFilter;
      if (
        deleteOperationsByFilterResult?.__typename ===
        'DeleteOperationsByFilterResult'
      ) {
        onClose();
        dispatchBulkSelectAction({ type: 'reset' });
      } else if (
        deleteOperationsByFilterResult?.__typename === 'PermissionError' ||
        !deleteOperationsByFilterResult
      ) {
        setErrorMessage(
          deleteOperationsByFilterResult?.message ?? 'An error has occurred',
        );
      } else {
        setErrorMessage(
          assertUnreachableOrReturnDefault(
            deleteOperationsByFilterResult,
            'An error has occurred',
          ),
        );
      }
    } else {
      setErrorMessage(
        assertUnreachableOrReturnDefault(
          bulkSelectState,
          'An error has occurred',
        ),
      );
    }
  }, [
    bulkSelectState,
    deleteIndividualOperations,
    graphId,
    listId,
    trackCustomEvent,
    onClose,
    dispatchBulkSelectAction,
    deleteBulkOperations,
    filters,
  ]);

  const hasMultipleOperations =
    bulkSelectState.state === 'bulk' || bulkSelectState.toggledItems.length > 1;

  const isLoading =
    deleteIndividualOperationsLoading || deleteBulkOperationsLoading;

  return (
    <Modal isOpen={isOpen} size="xl" onClose={onClose}>
      <ModalOverlay />
      <FormWrapper onSubmit={onSubmit} noValidate>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {hasMultipleOperations
                ? 'Are you sure you want to delete these operations?'
                : 'Are you sure you want to delete this operation?'}
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            {!!errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            <p>
              {hasMultipleOperations ? (
                <>
                  Deleting these{' '}
                  <Text as="span" variant="bodySb">
                    {totalSelectedItems} operations
                  </Text>{' '}
                  will remove them from this persisted queries list.
                </>
              ) : (
                'Deleting this operation will remove it from this persisted queries list. '
              )}
              This is a destructive action that cannot be undone.
            </p>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                isLoading={isLoading}
                loadingText="Deleting..."
                type="submit"
              >
                Yes, delete
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </FormWrapper>
    </Modal>
  );
};
